import type { ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../layouts/DashboardLayout';
import { guestMayOpenLesson, guestMayOpenPath, guestMayPracticeTopic } from '../../lib/onboardingStore';

export default function CourseLessonRoute({
  kind,
  children,
}: {
  kind: 'lesson' | 'practice';
  children: ReactNode;
}) {
  const { user, loading, profileComplete } = useAuth();
  const { slug = '', lessonSlug = '', mode } = useParams();
  const guest = kind === 'practice' ? guestMayPracticeTopic(slug, mode) : guestMayOpenLesson(slug, lessonSlug);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!user) {
    if (!guest) return <Navigate to="/auth" replace />;
    return <div className="min-h-screen overflow-x-hidden bg-white px-4 py-4">{children}</div>;
  }

  if (!profileComplete) return <Navigate to="/profile-setup" replace />;

  return <DashboardLayout>{children}</DashboardLayout>;
}

export function CoursePathRoute({ children }: { children: ReactNode }) {
  const { user, loading, profileComplete } = useAuth();
  const { slug = '' } = useParams();
  const guest = guestMayOpenPath(slug);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!user) {
    if (!guest) return <Navigate to="/auth" replace />;
    return <div className="min-h-screen overflow-x-hidden bg-white px-4 py-4">{children}</div>;
  }

  if (!profileComplete) return <Navigate to="/profile-setup" replace />;
  return <DashboardLayout>{children}</DashboardLayout>;
}

function GuestTabs() {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white md:hidden" aria-label="Main">
      <div className="mx-auto flex h-16 max-w-lg items-stretch">
        <Link to="/home" className="flex flex-1 items-center justify-center text-sm font-semibold text-gray-700">Home</Link>
        <Link to="/explore" className="flex flex-1 items-center justify-center text-sm font-semibold text-gray-900">Explore</Link>
        <Link to="/me" className="flex flex-1 items-center justify-center text-sm font-semibold text-gray-900">Me</Link>
      </div>
    </nav>
  );
}

/** Guests see the page. Signed-in people use the dashboard, or finish profile setup first. */
export function PublicAccountRoute({ children }: { children: ReactNode }) {
  const { user, loading, profileComplete } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-teal-600" />
      </div>
    );
  }

  if (user && !profileComplete) return <Navigate to="/profile-setup" replace />;
  if (user) return <DashboardLayout>{children}</DashboardLayout>;
  return (
    <div className="min-h-screen overflow-x-hidden bg-stone-50 pb-20">
      {children}
      <GuestTabs />
    </div>
  );
}
